import React from "react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { BsMicFill } from "react-icons/bs";
import { scroller } from "react-scroll";

const Hero = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.8,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const scrollToProcess = () => {
    scroller.scrollTo("Process", {
      smooth: true,
      duration: 800,
      offset: -80,
    });
  };

  return (
    <div
      id="Hero"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(to right, #000000, #434343)",
        color: "white",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <motion.div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "4rem 1rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          "@media (max-width: 768px)": {
            maxWidth: "100%", // Allow full width on mobile
            padding: "2rem 0.5rem", // Reduce padding to give more space
            width: "100%",
            overflow: "visible",
          },
        }}
        variants={prefersReducedMotion ? {} : containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Column */}
        <motion.div
          style={{
            width: "100%",
            textAlign: "center",
            marginBottom: "3rem",
          }}
          variants={prefersReducedMotion ? {} : itemVariants}
        >
          <h1
            style={{
              fontSize: "4.6rem",
              fontWeight: "800",
              marginBottom: "1.5rem",
              lineHeight: "1.2",
              fontFamily: "Poppins, sans-serif",
              textAlign: "center",
              marginTop: "7%",
              maxWidth: "100%",
              width: "100%", // Ensure full width
              overflowWrap: "break-word",
              "@media (max-width: 768px)": {
                fontSize: "1.8rem", // Reduced to ensure fit
                marginTop: "12%",
                display: "block", // Simplify to block on mobile
                width: "100%",
                overflow: "visible",
                padding: "0",
                lineHeight: "1",
              },
            }}
          >
            {/* Single array for all words */}
            {["Create", "Your", "Perfect", "Podcast"].map((word, wordIndex) => (
              <React.Fragment key={wordIndex}>
                <span
                  style={{
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    marginRight:
                      wordIndex === 0 || wordIndex === 2 ? "0.4em" : "0", // Space between "Create" and "Your", "Perfect" and "Podcast"
                    "@media (max-width: 768px)": {
                      display: "block", // Each word on its own line on mobile
                      whiteSpace: "normal",
                      width: "auto",
                      textAlign: "center",
                      marginRight: "0", // Remove horizontal spacing on mobile
                      marginBottom: "0.5rem", // Add vertical spacing between words on mobile
                      overflow: "visible",
                    },
                  }}
                >
                  {word.split("").map((char, charIndex) => (
                    <motion.span
                      key={charIndex}
                      style={{
                        display: "inline-block",
                        backgroundImage: "linear-gradient(to right, white, gray)",
                        WebkitBackgroundClip: "text",
                        color: wordIndex < 2 ? "#fdac00" : "#fdac00", // Color for "Create Your" vs "Perfect Podcast"
                        padding: "0 1px",
                        whiteSpace: "normal",
                        "@media (max-width: 768px)": {
                          display: "inline-block", // Ensure characters stay inline within the word
                          whiteSpace: "normal",
                        },
                      }}
                      whileHover={prefersReducedMotion ? {} : { y: -15 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 10,
                      }}
                    >
                      {char}
                    </motion.span>
                  ))}
                </span>
                {/* Add a line break after "Your" (index 1) on desktop */}
                {wordIndex === 1 && (
                  <br
                    style={{
                      "@media (max-width: 768px)": {
                        display: "none",
                      },
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </h1>
          <motion.p
            style={{
              fontSize: "1.25rem",
              color: "gray",
              marginBottom: "2rem",
              fontFamily: "Roboto, sans-serif",
              "@media (max-width: 768px)": {
                fontSize: "1rem",
                padding: "0 1rem",
              },
            }}
            variants={prefersReducedMotion ? {} : itemVariants}
          >
            Professional studio quality recording at your fingertips
          </motion.p>
          <motion.button
            style={{
              padding: "1rem 2rem",
              border: "2px solid #c31b48",
              borderRadius: "9999px",
              fontSize: "1.125rem",
              fontWeight: "600",
              transition: "all 0.3s",
              backgroundColor: "transparent",
              color: "#fdac00",
              cursor: "pointer",
              "@media (max-width: 768px)": {
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
              },
            }}
            whileHover={{
              scale: 1.05,
              backgroundColor: "#fdac00",
              color: "black",
              boxShadow: "0 0 20px rgba(255,255,255,0.3)",
            }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToProcess}
          >
            Get Started
          </motion.button>
        </motion.div>

        {/* Right Column */}
        <motion.div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
          variants={prefersReducedMotion ? {} : itemVariants}
        >
          <div
            style={{
              position: "relative",
              width: "23rem",
              height: "23rem",
              "@media (max-width: 768px)": {
                width: "15rem",
                height: "15rem",
              },
            }}
          >
            <motion.div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              animate={
                prefersReducedMotion
                  ? {}
                  : {
                      rotate: [0, 5, -5, 0],
                      y: [0, -10, 10, 0],
                    }
              }
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "linear",
              }}
            >
              <BsMicFill
                style={{
                  width: "15rem",
                  height: "15rem",
                  color: "#fdac00",
                  opacity: 0.8,
                  "@media (max-width: 768px)": {
                    width: "10rem",
                    height: "10rem",
                  },
                }}
              />
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      {/* Background Effect */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default Hero;